import type { User } from "firebase/auth";
import { APEX_SESSION_ENDPOINT_URL } from "@/lib/apexSessionEndpoint";
import { recordApexSessionObserved } from "@/lib/apexObservedState";

/**
 * KB-3235 — mint the apex (`kidsbooked.com`) `__session` cookie after a pro
 * sign-in, so a director signed in at pro is also recognised on the consumer
 * site without signing in a second time. This is the REVERSE of KB-3228's
 * apex→pro door (`apexSsoBootstrap.ts`), pointed the other way: there we adopt
 * an existing apex session INTO pro; here we project a fresh pro session OUT to
 * apex.
 *
 * The endpoint already exists and is already CORS-wired for pro's origin
 * (KB-3227 added `https://pro.kidsbooked.com` to marketing's `ALLOWED_ORIGINS`;
 * `session.ts` has carried `OPTIONS`/`applyCorsHeaders` since KB-187). So this is
 * pro-side only: POST the pro user's Firebase ID token to the apex session
 * endpoint with the browser's cookies, and it Set-Cookies the apex `__session`.
 *
 * ## Fire-and-forget — the caller must NOT await this (AC-2)
 *
 * Pro's own session is the one that matters; the apex cookie is an enhancement
 * layered on top of it. A failed or hung mint must neither FAIL nor DELAY the
 * pro sign-in. Every call site therefore invokes this WITHOUT awaiting (`void
 * mintApexSession(user)`), so the sign-in proceeds to its `navigate()` on the
 * very next line regardless of what happens here. Two consequences are load-
 * bearing and are pinned by tests:
 *
 *  - This function NEVER throws. Every failure — a rejected fetch, a non-2xx
 *    response, a `getIdToken` that rejects, the abort below — resolves to an
 *    `ApexMintOutcome` and a single `console.warn`, never a user-facing error.
 *  - The fetch is bounded (`AbortController` + `APEX_SESSION_MINT_BUDGET_MS`,
 *    mirroring `apexSsoBootstrap.ts`'s `APEX_SSO_FETCH_BUDGET_MS`) so a hung apex
 *    endpoint cannot leak a forever-pending request. `getIdToken` is not
 *    separately bounded because nothing awaits this promise — a slow token
 *    refresh delays only the (discarded) mint, never the sign-in.
 *
 * ## Only after the gate returns `allowed` (AC-4, the blocking one)
 *
 * This is wired at each of the five sign-in doors AFTER `enforceProAccessGate`
 * has returned `allowed` (on the apex-sso door, inside the
 * `verdict.outcome === "allowed"` branch). Minting for a caller the gate then
 * refuses would hand an apex session to someone pro just rejected — the same
 * class of mistake KB-3064 and KB-3133 were about: the authorization check must
 * precede anything that grants reach. The placement IS the invariant, and it is
 * red-proved for the apex-sso door in `apexSsoBootstrap.test.ts`.
 */

/**
 * The apex session-cookie endpoint (CORS-wired for pro by KB-3227 / KB-187).
 * The literal lives in the dependency-free leaf `apexSessionEndpoint.ts` so the
 * mint and revoke share ONE URL without coupling their (mockable) behaviour
 * modules — see that module's docblock (KB-3270). Re-exported here for back-compat
 * with existing importers of `APEX_SESSION_MINT_URL`.
 */
export const APEX_SESSION_MINT_URL = APEX_SESSION_ENDPOINT_URL;

/**
 * How long to wait on the apex mint before aborting. The mint is un-awaited, so
 * this bounds only the leaked-request risk, not the sign-in. Mirrors
 * `apexSsoBootstrap.ts`'s fetch budget.
 */
export const APEX_SESSION_MINT_BUDGET_MS = 5000;

/**
 * What the mint attempt resolved to. `minted` means the apex `__session` cookie
 * was Set. `rejected` is a reached-but-refused endpoint (non-2xx — e.g. the
 * token was too old for the 100-char floor, or Firebase rejected it). `error`
 * is anything that threw: a network/abort failure or a `getIdToken` rejection.
 * Kept distinct so tests can tell "the endpoint said no" from "we never reached
 * it"; all three are swallowed identically at the call site.
 */
export type ApexMintOutcome = "minted" | "rejected" | "error";

/**
 * Mint the apex `__session` cookie from a signed-in pro user's ID token.
 * Fire-and-forget: never throws, never blocks the caller. See the module
 * docblock for why the caller must not await it.
 */
export async function mintApexSession(user: User): Promise<ApexMintOutcome> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), APEX_SESSION_MINT_BUDGET_MS);
  try {
    const idToken = await user.getIdToken();
    const res = await fetch(APEX_SESSION_MINT_URL, {
      method: "POST",
      credentials: "include",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ idToken }),
      signal: controller.signal,
    });
    if (!res.ok) {
      // Reached the endpoint and it refused. Warn (not error, to stay out of
      // Sentry noise) — the pro sign-in is unaffected and there is nothing the
      // user can or should do about a missing consumer-site cookie.
      console.warn(`Apex session mint: endpoint returned ${res.status}`);
      return "rejected";
    }
    // KB-3271 AC-1: we just created a live apex session for this uid — arm the
    // reverse checker so a later apex sign-out ends this pro session.
    recordApexSessionObserved(user.uid);
    return "minted";
  } catch (mintError) {
    // Network failure, abort on the budget, or a rejected getIdToken. Swallowed
    // deliberately: this is an enhancement to a sign-in that has already
    // succeeded, so its failure must be silent to the user (AC-2).
    console.warn("Apex session mint failed:", mintError);
    return "error";
  } finally {
    clearTimeout(timer);
  }
}
